import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { LoginCallback } from '@okta/okta-react';
import Navbar from './components/Navbar';
import OktaLoginPage from './components/OktaLoginPage';
import Dashboard from './pages/Dashboard/Dashboard';
import UploadPage from './pages/UploadPage/UploadPage';
import History from './pages/History/History';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard.jsx';
import Support from './pages/Support/Support';
import Logout from './pages/Logout/Logout';
import LoadingSpinner from './components/common/LoadingSpinner';
import { useAuth } from './contexts/AuthContext';

// Graceful error recovery for LoginCallback failures (e.g. PKCE codeVerifier missing).
// Clears the redirect guard and sends the user back to /login for a fresh flow.
const LoginCallbackError = ({ error }) => {
  const navigate = useNavigate();

  React.useEffect(() => {
    console.warn('Okta LoginCallback error:', error?.message);
    // Remove the PKCE redirect guard so OktaLoginPage can restart the flow cleanly
    sessionStorage.removeItem('okta_pkce_redirect_in_progress');
    navigate('/login', { replace: true });
  }, [error, navigate]);

  return <LoadingSpinner size="large" overlay={true} />;
};

LoginCallbackError.propTypes = {
  error: PropTypes.instanceOf(Error)
};

// Component to handle logged-in users visiting /login
const LoggedInLoginRedirect = ({ isAdminUser }) => {
  const targetPath = (isAdminUser)
    ? '/admin-dashboard'
    : '/dashboard';
  console.log('LoggedInLoginRedirect: isAdminUser =', isAdminUser);
  return <Navigate to={targetPath} replace />;
};

LoggedInLoginRedirect.propTypes = {
  isAdminUser: PropTypes.bool
};

// Route guard to protect admin-only routes
const AdminRouteGuard = ({ children, isAdminUser }) => {
  if (!isAdminUser) {
    console.log('AdminRouteGuard: Non-admin user attempting to access admin route, redirecting to /dashboard');
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

AdminRouteGuard.propTypes = {
  children: PropTypes.node.isRequired,
  isAdminUser: PropTypes.bool
};

// Route guard to protect client routes from admin users
const ClientRouteGuard = ({ children, isAdminUser }) => {
  if (isAdminUser) {
    console.log('ClientRouteGuard: Admin user attempting to access client route, redirecting to /admin-dashboard');
    return <Navigate to="/admin-dashboard" replace />;
  }
  return children;
};

ClientRouteGuard.propTypes = {
  children: PropTypes.node.isRequired,
  isAdminUser: PropTypes.bool
};

function AppRoutes({ clientName, setClientName, userRole, setUserRole }) {
  const location = useLocation();
  const { isLoggedIn, logout, isExternal, user, isInitializing, error } = useAuth();
  const isAdminUser = isExternal === null ? null : !isExternal;

  if (isInitializing && location.pathname !== '/login/callback') {
    return <LoadingSpinner size="large" overlay={true} />;
  }

  // If there's an AuthContext error (e.g., unauthorized user), render the login page
  // directly so the error message is visible and persists.
  if (error && location.pathname !== '/login/callback') {
    return <OktaLoginPage />;
  }

  const handleLogout = async () => {
    setClientName?.('');
    setUserRole?.('');

    // Call logout from AuthContext (handles Okta sign out)
    try {
      console.log('Calling AuthContext logout...');
      await logout();
      console.log('AuthContext logout completed successfully');
    } catch (error) {
      console.error('AuthContext logout failed:', error);
    }
  };

  // Hide navbar for admin users and on specific pages
  const hideNavbar = ['/set-password', '/change-password'].includes(location.pathname) || isAdminUser || (isLoggedIn && isExternal === null);

  return (
    <>
      {isLoggedIn ? (
        <>
          {!hideNavbar && (
            <Navbar firstName={user?.firstName} lastName={user?.lastName} onLogout={handleLogout} />
          )}
          <Routes>
            {/* Client routes - protected from admin users */}
            <Route path="/dashboard" element={
              <ClientRouteGuard isAdminUser={isAdminUser}>
                <Dashboard userDetails={user} />
              </ClientRouteGuard>
            } />
            <Route path="/upload" element={
              <ClientRouteGuard isAdminUser={isAdminUser}>
                <UploadPage organizationId={user?.organizationId} userId={user?.userId} />
              </ClientRouteGuard>
            } />
            <Route path="/upload-history" element={
              <ClientRouteGuard isAdminUser={isAdminUser}>
                <History organizationId={user?.organizationId} />
              </ClientRouteGuard>
            } />
            <Route path="/support" element={
              <ClientRouteGuard isAdminUser={isAdminUser}>
                <Support />
              </ClientRouteGuard>
            } />

            {/* Admin routes - protected from non-admin users */}
            <Route path="/admin-dashboard" element={
              <AdminRouteGuard isAdminUser={isAdminUser}>
                <AdminDashboard userDetails={user} />
              </AdminRouteGuard>
            } />

            {/* Logout route - accessible to all authenticated users */}
            <Route path="/login/signout" element={<Logout />} />

            {/* Redirect logged-in users away from login page */}
            <Route path="/login" element={<LoggedInLoginRedirect isAdminUser={isAdminUser} />} />
            
            {/* Login callback is public but will redirect if already authenticated */}
            <Route path="/login/callback" element={<LoginCallback errorComponent={LoginCallbackError} />} />

            {/* Default route for all other paths */}
            <Route path="/*" element={
              isAdminUser
                ? <Navigate to="/admin-dashboard" replace />
                : <Navigate to="/dashboard" replace />
            } />
          </Routes>
        </>
      ) : (
        <Routes>
          {/* Public routes when not authenticated */}
          <Route path="/login" element={<OktaLoginPage />} />
          <Route path="/login/callback" element={<LoginCallback errorComponent={LoginCallbackError} />} />
          {/* Redirect root path to login */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          {/* Redirect all other routes to login when not authenticated */}
          <Route path="/*" element={<Navigate to="/login" replace />} />
        </Routes>
      )}
    </>
  );
}

AppRoutes.propTypes = {
  clientName: PropTypes.string,
  setClientName: PropTypes.func,
  userRole: PropTypes.string,
  setUserRole: PropTypes.func
};

export default AppRoutes;
